// Validation for Save & Submit: which user-editable, currently-visible fields
// have not been answered. Mirrors the rendering visibility rules (TabPanel /
// Group / Question) so we only flag fields the user can actually see + answer.

import { isHidden } from "./rule";
import { stripHtml } from "./text";
import { isFah, isFahSectionGroupCode, fahSectionHideState } from "./fahSections";
import type { CpqData } from "../data/loader";
import type { AnswerState, Answer, Question } from "../types";
import { isManuallyHidden } from "./hiddenQuestions";
import { getProfile } from "../products";
import { isOptionalGroupForLaunch } from "./quoteScope";

// Groups suppressed per tab, per PRODUCT (kept in sync with TabPanel rendering --
// both read this same accessor, so a group cannot be hidden in one and required in
// the other). The codes live in that product's profile; see src/products/.
//
// Keying by tab code ALONE was not safe: "MAIN" is generic enough that two
// configurators can both have a tab by that name, and the second would silently
// inherit the first's suppressions.
export function hiddenGroupCodes(product: string, tabCode: string): ReadonlySet<string> {
  return getProfile(product).hiddenGroupCodesByTab[tabCode] ?? EMPTY_SET;
}

const EMPTY_SET: ReadonlySet<string> = new Set();

// Optional-as-a-pair rules come from the running configurator's profile
// (src/products/) -- they name that product's question codes, so they are product
// knowledge like every other set here.

// Fields that may NEVER be submitted blank — the "Save and Submit again to save
// anyway" bypass does not apply to these, the same way an invalid Housing Height
// is a hard block (see findInvalidHousingHeight). Per product; see src/products/.
export function isHardRequired(product: string, code: string): boolean {
  return getProfile(product).hardRequiredQuestionCodes.has(code);
}

export type UnansweredField = {
  code: string;
  label: string;
  tab: string;
  tabId: string;
};

function answerVisible(a: Answer, answers: AnswerState): boolean {
  return !isHidden(
    {
      hideRule: a.custrecord_cpqc_answer_hide_rule,
      parsedHideRule: a.custrecord_cpqc_answer_parsed_hide_rule,
    },
    answers
  );
}

// `visibleAns` is the question's currently-selectable answers (same filter the
// widgets use). Returns true if the field has a value the user provided.
//
// `fallbackFor` is the profile's unlistedSizeTextFallback. A combo box on that map
// holds a size no option represents as FREE TEXT elsewhere (Flange to Nozzle End ->
// EXT_NIP), and the question itself is then deliberately unanswered. It is still
// filled in as far as the user is concerned, and requiring an option that does not
// exist for the size they need would block Save & Submit with no way out — which is
// exactly what the box is there to avoid.
function isAnswered(
  q: Question,
  visibleAns: Answer[],
  answers: AnswerState,
  fallbackFor: ReadonlyMap<string, readonly [string, string]> = new Map()
): boolean {
  const code = q.custrecord_cpqc_question_code;
  const fb = fallbackFor.get(code);
  if (fb) {
    const held = answers[`${fb[0]}/${fb[1]}`];
    if (held !== undefined && held !== null && String(held).trim() !== "") return true;
  }
  if (q.custrecord_cpqc_question_type === "4") {
    // a dropdown is answered when one of its options is selected (true)
    return visibleAns.some(
      (a) => answers[`${code}/${a.custrecord_cpqc_answer_code}`] === true
    );
  }
  // type 7 (text): answered when its answer slot holds a non-empty value
  const a0 = visibleAns[0];
  const v = answers[`${code}/${a0.custrecord_cpqc_answer_code}`];
  return v !== undefined && v !== null && String(v).trim() !== "";
}

const bySeq =
  <T>(key: keyof T) =>
  (a: T, b: T) =>
    Number((a[key] as unknown as string) || 0) -
    Number((b[key] as unknown as string) || 0);

/** A field the user must answer right now, and the section it sits in (a section's
 *  Calculate button reads that: it is the next step once its own section is filled). */
export type RequiredField = UnansweredField & { answered: boolean; groupId: string };

/** Every field the user must answer right now, each flagged answered or not.
 *  ONE traversal backs both the Save & Submit check (the unanswered subset) and
 *  the completion progress bar (the ratio), so the bar can never disagree with
 *  what Save & Submit demands — 100% means "submitting will not flag anything". */
export function collectRequiredVisible(
  data: CpqData,
  answers: AnswerState,
  // asSalesOrder: judge the form the way a sales order would, ignoring the sections a
  // QUOTE marks "(optional)". Only util/unfinishedConfig wants this -- an item finished
  // for a quote is still unfinished for the build that a sales order turns it into.
  // requiredCells: table cells ("IN/MAIN_1") that must be filled although table questions
  // otherwise are not checked -- an MB middle burner's In / Up distances, which its mirror
  // could not know (AnswersContext mirrorRequiredCells). Each is required like any field of its
  // section, and only while its inlet is in use (a size other than None).
  opts: { asSalesOrder?: boolean; requiredCells?: readonly string[] } = {}
): RequiredField[] {
  // This configurator's own rules. Nothing below may consult another product's.
  const profile = getProfile(data.slug);
  // Answered-by-code lookup, so an optional PAIR can ask about its sibling.
  const byCode = new Map<string, Question>(
    data.questions.map((q) => [q.custrecord_cpqc_question_code, q])
  );
  const isAnsweredByCode = (code: string): boolean => {
    const q = byCode.get(code);
    if (!q) return false;
    const vis = (q._answers ?? []).filter((a) => answerVisible(a, answers));
    return vis.length > 0 && isAnswered(q, vis, answers, profile.unlistedSizeTextFallback);
  };
  const partnerOf = new Map<string, string>();
  for (const [a, b] of profile.optionalQuestionPairs) {
    partnerOf.set(a, b);
    partnerOf.set(b, a);
  }
  // A pair member is optional only while the WHOLE pair is unused (both blank).
  const isUnusedPairMember = (code: string): boolean => {
    const partner = partnerOf.get(code);
    return (
      partner !== undefined &&
      !isAnsweredByCode(code) &&
      !isAnsweredByCode(partner)
    );
  };

  const cellsByQuestion = new Map<string, string[]>();
  for (const key of opts.requiredCells ?? []) {
    const i = key.indexOf("/");
    if (i < 0) continue;
    const q = key.slice(0, i);
    cellsByQuestion.set(q, [...(cellsByQuestion.get(q) ?? []), key.slice(i + 1)]);
  }
  const cellInUse = (cell: string) => {
    const size = String(answers[`SIZE_TABLE/${cell}`] ?? "").trim().toLowerCase();
    return size !== "" && size !== "none";
  };

  const out: RequiredField[] = [];
  // Traverse tabs → groups → questions in the SAME sequence order the UI renders
  // (Configurator/TabPanel/Group all sort by *_sequence), so out[0] is the
  // visually-first flagged field — the one Save & Submit scrolls the user to.
  const tabsSorted = [...data.tabs].sort(bySeq<(typeof data.tabs)[number]>("custrecord_cpqc_tab_sequence"));
  for (const tab of tabsSorted) {
    if (tab.custrecord_cpqc_tab_code === "DEBUG") continue;
    if (
      isHidden(
        {
          hideRule: tab.custrecord_cpqc_tab_hide_rule,
          parsedHideRule: tab.custrecord_cpqc_tab_parsed_hide_rule,
        },
        answers
      )
    )
      continue;
    const suppressed = hiddenGroupCodes(data.slug, tab.custrecord_cpqc_tab_code);
    const groupsSorted = [...(tab._groups ?? [])].sort(
      bySeq<NonNullable<typeof tab._groups>[number]>("custrecord_cpqc_group_sequence")
    );
    for (const g of groupsSorted) {
      const gcode = g.custrecord_cpqc_group_code;
      if (suppressed.has(gcode)) continue;
      // Optional on a QUOTE: shown, headed "(optional)", and not counted as missing. The
      // heading and this skip read the same rule, so a section cannot say optional and then
      // block the submit.
      if (!opts.asSalesOrder && isOptionalGroupForLaunch(tab.custrecord_cpqc_tab_code, gcode)) continue;
      // FAH sections (Main Burner / Pilot / Ignitor) are evaluated with the
      // "no main burner" signal cleared — same shadow the UI renders with — so
      // validation sees exactly what the user can see; `stateForHide` is the
      // untouched `answers` for every other group.
      const fahSection = isFah(answers) && isFahSectionGroupCode(data.slug, gcode);
      const stateForHide = fahSectionHideState(gcode, answers);
      if (
        isHidden(
          {
            hideRule: g.custrecord_cpqc_group_hide_rule,
            parsedHideRule: g.custrecord_cpqc_group_parsed_hide_rule,
          },
          stateForHide
        )
      )
        continue;
      const qs = data.questions
        .filter(
          (q) =>
            q.custrecord_cpqc_question_tab === tab.id &&
            q.custrecord_cpqc_question_group === g.id
        )
        .sort(bySeq<Question>("custrecord_cpqc_question_sequence"));
      // FAH: the Main Burner / Pilot / Ignitor sections are optional AS A WHOLE.
      // Only once the user fills ANY field in a section do the rest of that
      // section's visible fields become required; an untouched FAH section is
      // skipped entirely (no message/highlight). Non-FAH is unchanged.
      if (fahSection) {
        const touched = qs.some((q) => {
          const t = q.custrecord_cpqc_question_type;
          if (t !== "4" && t !== "7") return false;
          const vis = (q._answers ?? []).filter((a) =>
            answerVisible(a, stateForHide)
          );
          return vis.length > 0 && isAnswered(q, vis, answers, profile.unlistedSizeTextFallback);
        });
        if (!touched) continue;
      }
      for (const q of qs) {
        const type = q.custrecord_cpqc_question_type;
        const code = q.custrecord_cpqc_question_code;
        const cells = cellsByQuestion.get(code);
        if (cells) {
          const hidden = isHidden(
            { hideRule: q.custrecord_cpqc_question_hide_rule, parsedHideRule: q.custrecord_cpqc_question_p_hide_rule },
            stateForHide
          );
          if (!hidden) {
            const qLabel = stripHtml(q.custrecord_cpqc_question_label_html) || q.name;
            for (const cell of cells.filter(cellInUse)) {
              const col = (q._answers ?? []).find((a) => a.custrecord_cpqc_answer_code === cell.replace(/_\d+$/, ""));
              const v = answers[`${code}/${cell}`];
              out.push({
                code,
                label: `${qLabel} (${col ? stripHtml(col.custrecord_cpqc_answer_label_html) || col.name : cell})`,
                tab: tab.name,
                tabId: tab.id,
                groupId: g.id,
                answered: v !== undefined && v !== null && String(v).trim() !== "",
              });
            }
          }
          continue;
        }
        if (type !== "4" && type !== "7") continue; // user-editable value inputs
        if (profile.optionalQuestionCodes.has(code)) continue;
        // Hidden from the UI (util/hiddenQuestions.ts): the user cannot see or
        // answer it, so requiring it would block Save & Submit with no way out.
        // Scoped to this configurator, because the same question code can be a
        // hidden field in one product and a required one in another.
        if (isManuallyHidden(data.slug, code)) continue;
        if (isUnusedPairMember(code)) continue;
        if (
          isHidden(
            {
              hideRule: q.custrecord_cpqc_question_hide_rule,
              parsedHideRule: q.custrecord_cpqc_question_p_hide_rule,
            },
            stateForHide
          )
        )
          continue;
        // Only the currently-selectable answers count — mirrors the widgets.
        const visibleAns = (q._answers ?? []).filter((a) =>
          answerVisible(a, stateForHide)
        );
        // A field with no selectable answers can't be answered — skip it.
        if (visibleAns.length === 0) continue;
        out.push({
          code,
          label: stripHtml(q.custrecord_cpqc_question_label_html) || q.name,
          tab: tab.name,
          tabId: tab.id,
          groupId: g.id,
          answered: isAnswered(q, visibleAns, answers, profile.unlistedSizeTextFallback),
        });
      }
    }
  }
  return out;
}

/** Which visible, user-editable fields still need an answer. Order matches the
 *  UI, so [0] is the visually-first flagged field Save & Submit scrolls to. */
export function findUnansweredVisible(
  data: CpqData,
  answers: AnswerState,
  requiredCells?: readonly string[]
): UnansweredField[] {
  return collectRequiredVisible(data, answers, { requiredCells })
    .filter((f) => !f.answered)
    .map(({ code, label, tab, tabId }) => ({ code, label, tab, tabId }));
}

/** The subset of unanswered fields that BLOCK submitting outright — no bypass.
 *  Visibility is decided by the same traversal as everything else, so a field
 *  that isn't currently shown can't block. */
export function findBlockingUnanswered(
  data: CpqData,
  answers: AnswerState
): UnansweredField[] {
  return findUnansweredVisible(data, answers).filter((f) => isHardRequired(data.slug, f.code));
}

/** Per-tab completion, in the tab order the UI renders. */
export type TabProgress = { tab: string; tabId: string; total: number; answered: number };

/** Every question the user can SEE on each tab, answered or not -- what the tab pills show.
 *  Kevin, 2026-10-19: "the firetube tab is showing 13/14 when there is more than 14 questions
 *  on the firetube tab." The required-field count (getCompletionProgress) skips optional
 *  questions, untouched FAH sections and the sections a quote marks optional, which is right
 *  for "may I submit" and wrong for "how much of this page is filled". A table counts as one
 *  question, answered when any of its cells holds a value. Tabs are listed the way
 *  getCompletionProgress lists them, so Firetube is there from the start. */
/** The question codes the user can see right now (visible tab, group and question). The
 *  Save & Submit comparison of prediction vs configuration counts only these: an FAH hides
 *  its burner sections and a Box its hinge, and a vote on a hidden question is not a miss. */
export function visibleQuestionCodes(data: CpqData, answers: AnswerState): ReadonlySet<string> {
  const out = new Set<string>();
  for (const tab of data.tabs) {
    if (tab.custrecord_cpqc_tab_code === "DEBUG") continue;
    if ((tab.custrecord_cpqc_tab_parsed_hide_rule ?? "").trim() === "true") continue;
    if (isHidden({ hideRule: tab.custrecord_cpqc_tab_hide_rule, parsedHideRule: tab.custrecord_cpqc_tab_parsed_hide_rule }, answers)) continue;
    const suppressed = hiddenGroupCodes(data.slug, tab.custrecord_cpqc_tab_code);
    for (const g of tab._groups ?? []) {
      const gcode = g.custrecord_cpqc_group_code;
      if (suppressed.has(gcode)) continue;
      const stateForHide = fahSectionHideState(gcode, answers);
      if (isHidden({ hideRule: g.custrecord_cpqc_group_hide_rule, parsedHideRule: g.custrecord_cpqc_group_parsed_hide_rule }, stateForHide)) continue;
      for (const q of data.questions) {
        if (q.custrecord_cpqc_question_tab !== tab.id || q.custrecord_cpqc_question_group !== g.id) continue;
        const code = q.custrecord_cpqc_question_code;
        if (isManuallyHidden(data.slug, code)) continue;
        if (isHidden({ hideRule: q.custrecord_cpqc_question_hide_rule, parsedHideRule: q.custrecord_cpqc_question_p_hide_rule }, stateForHide)) continue;
        out.add(code);
      }
    }
  }
  return out;
}

export function getVisibleQuestionProgress(data: CpqData, answers: AnswerState): TabProgress[] {
  const profile = getProfile(data.slug);
  const seen = new Map<string, TabProgress>();
  const tabsSorted = [...data.tabs].sort(bySeq<(typeof data.tabs)[number]>("custrecord_cpqc_tab_sequence"));
  for (const tab of tabsSorted) {
    if (tab.custrecord_cpqc_tab_code === "DEBUG") continue;
    const parsed = (tab.custrecord_cpqc_tab_parsed_hide_rule ?? "").trim();
    if (parsed === "true") continue;
    const t: TabProgress = { tab: tab.name, tabId: tab.id, total: 0, answered: 0 };
    seen.set(tab.id, t);
    if (isHidden({ hideRule: tab.custrecord_cpqc_tab_hide_rule, parsedHideRule: tab.custrecord_cpqc_tab_parsed_hide_rule }, answers)) continue;
    const suppressed = hiddenGroupCodes(data.slug, tab.custrecord_cpqc_tab_code);
    for (const g of tab._groups ?? []) {
      const gcode = g.custrecord_cpqc_group_code;
      if (suppressed.has(gcode)) continue;
      const stateForHide = fahSectionHideState(gcode, answers);
      if (isHidden({ hideRule: g.custrecord_cpqc_group_hide_rule, parsedHideRule: g.custrecord_cpqc_group_parsed_hide_rule }, stateForHide)) continue;
      for (const q of data.questions) {
        if (q.custrecord_cpqc_question_tab !== tab.id || q.custrecord_cpqc_question_group !== g.id) continue;
        const type = q.custrecord_cpqc_question_type;
        const code = q.custrecord_cpqc_question_code;
        if (type !== "4" && type !== "7" && type !== "11") continue;
        if (isManuallyHidden(data.slug, code)) continue;
        if (isHidden({ hideRule: q.custrecord_cpqc_question_hide_rule, parsedHideRule: q.custrecord_cpqc_question_p_hide_rule }, stateForHide)) continue;
        t.total++;
        if (type === "11") {
          const prefix = `${code}/`;
          if (Object.entries(answers).some(([k, v]) => k.startsWith(prefix) && v !== null && v !== undefined && v !== false && String(v).trim() !== "")) t.answered++;
          continue;
        }
        const vis = (q._answers ?? []).filter((a) => answerVisible(a, stateForHide));
        if (vis.length > 0 && isAnswered(q, vis, answers, profile.unlistedSizeTextFallback)) t.answered++;
      }
    }
  }
  return [...seen.values()];
}

/** Completion of the configuration, for the progress bar: how many of the fields
 *  that are required RIGHT NOW have been answered, overall AND split per tab.
 *
 *  The denominator moves as the form reveals and hides sections. That is honest —
 *  it reflects the work actually left rather than a fixed field count no single
 *  configuration ever reaches — but on its own it MISLEADS early: before a housing
 *  is picked, the Firetube tab contributes nothing, so a fresh form sits at 7/12
 *  (58%) with the great majority of the work still invisible, and the percentage
 *  then FALLS as Firetube reveals its fields (12 -> ~49 on a real configuration).
 *
 *  Hence `byTab`: the bar shows Customer Requirements and Firetube as separate
 *  running totals, so both tabs are always visibly accounted for and it is obvious
 *  where the remaining work lives. The overall figure still counts only what is
 *  required now, which preserves the property that 100% means "Save & Submit will
 *  not flag anything". */
export function getCompletionProgress(
  data: CpqData,
  answers: AnswerState,
  // the traversal, when the caller already has it (state/RequiredFields shares one per render)
  fields: RequiredField[] = collectRequiredVisible(data, answers)
): {
  total: number;
  answered: number;
  percent: number;
  remaining: number;
  byTab: TabProgress[];
} {
  const total = fields.length;
  const answered = fields.reduce((n, f) => n + (f.answered ? 1 : 0), 0);

  // Tabs to report, in render order. Every tab that can EVER apply is listed —
  // not just those visible right now — so Firetube appears from the start as
  // 0/0 and then fills, rather than materialising out of nowhere the moment a
  // housing is picked. Its tab hide rule is conditional
  // (HOUSING/HOUSING in O-Type | Hinge Type | Box-Type), which is exactly the
  // "later stage" the user needs to see coming.
  //
  // A tab whose parsed hide rule is the constant `true` is hidden
  // UNCONDITIONALLY — DEBUG, and today both Left/Right Firetube — so it can
  // never contribute and listing it would be permanent noise. Those are skipped.
  const seen = new Map<string, TabProgress>();
  for (const tab of [...data.tabs].sort(bySeq<(typeof data.tabs)[number]>("custrecord_cpqc_tab_sequence"))) {
    if (tab.custrecord_cpqc_tab_code === "DEBUG") continue;
    const parsed = (tab.custrecord_cpqc_tab_parsed_hide_rule ?? "").trim();
    if (parsed === "true") continue; // never shown at all
    seen.set(tab.id, { tab: tab.name, tabId: tab.id, total: 0, answered: 0 });
  }
  for (const f of fields) {
    const t = seen.get(f.tabId);
    if (!t) continue;
    t.total++;
    if (f.answered) t.answered++;
  }

  return {
    total,
    answered,
    remaining: total - answered,
    // No required fields yet (nothing revealed) reads as 0%, not 100% — an empty
    // form is not a complete one.
    percent: total === 0 ? 0 : Math.round((answered / total) * 100),
    byTab: [...seen.values()],
  };
}
